<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <!-- Navigation -->
    <nav class="bg-white shadow-sm border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <router-link to="/" class="flex items-center">
              <div class="flex-shrink-0">
                <h1 class="text-xl font-bold text-primary-600">Ballotshift</h1>
              </div>
            </router-link>
          </div>
          
          <div class="flex items-center space-x-4">
            <template v-if="authStore.isAuthenticated">
              <router-link
                to="/dashboard"
                class="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                :class="{ 'text-primary-600 bg-primary-50': $route.name === 'Dashboard' }"
              >
                Dashboard
              </router-link>
              <router-link
                to="/messages"
                class="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                :class="{ 'text-primary-600 bg-primary-50': $route.name === 'Messages' }"
              >
                Messages
              </router-link>
              <router-link
                to="/contacts"
                class="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                :class="{ 'text-primary-600 bg-primary-50': $route.name === 'Contacts' }"
              >
                Contacts
              </router-link>
              <button
                @click="handleLogout"
                class="text-gray-700 hover:text-error-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Logout
              </button>
            </template>
            <template v-else>
              <router-link
                to="/login"
                class="btn-primary"
              >
                Login
              </router-link>
            </template>
          </div>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="flex-1">
      <router-view />
    </main>

    <!-- Global Loading Overlay -->
    <div
      v-if="appStore.isLoading"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
    >
      <div class="bg-white p-6 rounded-lg shadow-lg">
        <div class="flex items-center space-x-3">
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span class="text-gray-700">Loading...</span>
        </div>
      </div>
    </div>

    <!-- Global Notification -->
    <div
      v-if="appStore.notification"
      class="fixed top-4 right-4 z-50 max-w-sm w-full"
    >
      <div
        class="rounded-md p-4 shadow-lg"
        :class="{
          'bg-success-50 border border-success-200': appStore.notification.type === 'success',
          'bg-error-50 border border-error-200': appStore.notification.type === 'error',
          'bg-warning-50 border border-warning-200': appStore.notification.type === 'warning',
          'bg-primary-50 border border-primary-200': appStore.notification.type === 'info'
        }"
      >
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              v-if="appStore.notification.type === 'success'"
              class="h-5 w-5 text-success-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else-if="appStore.notification.type === 'error'"
              class="h-5 w-5 text-error-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else-if="appStore.notification.type === 'warning'"
              class="h-5 w-5 text-warning-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else
              class="h-5 w-5 text-primary-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <div class="ml-3">
            <p
              class="text-sm font-medium"
              :class="{
                'text-success-800': appStore.notification.type === 'success',
                'text-error-800': appStore.notification.type === 'error',
                'text-warning-800': appStore.notification.type === 'warning',
                'text-primary-800': appStore.notification.type === 'info'
              }"
            >
              {{ appStore.notification.message }}
            </p>
          </div>
          <div class="ml-auto pl-3">
            <div class="-mx-1.5 -my-1.5">
              <button
                @click="appStore.clearNotification"
                class="inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2"
                :class="{
                  'text-success-500 hover:bg-success-100 focus:ring-success-600': appStore.notification.type === 'success',
                  'text-error-500 hover:bg-error-100 focus:ring-error-600': appStore.notification.type === 'error',
                  'text-warning-500 hover:bg-warning-100 focus:ring-warning-600': appStore.notification.type === 'warning',
                  'text-primary-500 hover:bg-primary-100 focus:ring-primary-600': appStore.notification.type === 'info'
                }"
              >
                <span class="sr-only">Dismiss</span>
                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from './stores/auth'
import { useAppStore } from './stores/app'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const appStore = useAppStore()
const router = useRouter()

const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/login')
    appStore.showNotification('Logged out successfully', 'success')
  } catch (error) {
    appStore.showNotification('Error logging out', 'error')
  }
}
</script>

